import { buttonVariants } from '@avanta/shared/components/ui/Button';
import { Textarea } from '@avanta/shared/components/ui/Textarea';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { motion } from 'framer-motion';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { chatReportSteps, StepComponentTypes } from 'constants/chat-report';
import { type Option, type StepType, type NestedOption } from 'types/chat-report';

export const Step = ({
  step,
  onNext,
}: {
  step: StepType;
  onNext: (newStep?: number, option?: string) => void;
}): React.ReactElement => {
  const { register, watch } = useFormContext();
  const etiology = watch(chatReportSteps.etiologyProblem);
  const fieldValue = watch(step.name);
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const STEP_KEY_MAP: Record<string, string> = {
    issueType: 'IssueType',
    etiologyProblem: 'EtiologyProblem',
    disorders: 'Disorders',
    reportObservations: 'Observations',
  };
  const stepKey = STEP_KEY_MAP[step.name] ?? step.name;

  const getOptions = (): Option | readonly string[] => {
    if (step.name === chatReportSteps.disorders && step.options) {
      const nestedOptions = step.options as NestedOption;
      return nestedOptions[etiology] || {};
    }
    return (step.options as Option) || {};
  };

  const handleOnClick = (option: string): void => {
    if (step.component === StepComponentTypes.radio) {
      onNext(step.id, option);
    }
  };

  const isChecked = (optionId: string): boolean => {
    if (step.component === StepComponentTypes.checkbox) {
      return Array.isArray(fieldValue) ? fieldValue.includes(optionId) : false;
    }
    return fieldValue === optionId;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-4"
    >
      <div className="bg-teal-200 text-teal-600 text-md font-medium rounded-2xl p-4 ">
        {t(`ChatReport.${stepKey}.heading`)}
      </div>
      <div className="grid gap-2">
        {step.component === StepComponentTypes.textarea ? (
          <Textarea {...register(step.name)} placeholder={t(`ChatReport.${stepKey}.placeholder`)} />
        ) : (
          Object.values(getOptions()).map(option => (
            <div key={option}>
              <input
                type={step.component}
                id={option}
                className="hidden"
                value={option}
                {...register(step.name)}
                onClick={() => handleOnClick(option)}
              />
              <label
                htmlFor={option}
                className={cn(
                  'hover:border-teal-600',
                  buttonVariants({ variant: 'terciary', className: 'w-full justify-start' }),
                  isChecked(option) && step.component === 'checkbox' && 'bg-mint-300'
                )}
              >
                {t(`ChatReport.${stepKey}.options.${option}`)}
              </label>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
};
